import { WhatsappLogo, PencilSimple, Trash, User, Warning } from '@phosphor-icons/react';
import { getPackageTheme } from '../utils/packageTheme';

/**
 * Google Calendar–like timeline for a single day.
 * Renders lessons as absolutely-positioned blocks over a 08:00 – 20:00 grid.
 * Overlapping lessons share horizontal space side-by-side.
 */
const START_HOUR = 8;
const END_HOUR = 20;
const HOUR_HEIGHT = 56; // px per hour
const TOTAL_HEIGHT = (END_HOUR - START_HOUR) * HOUR_HEIGHT;

function toMinutes(t) {
    if (!t) return 0;
    const [h, m] = t.split(':').map(Number);
    return h * 60 + (m || 0);
}

function computeLayout(lessons) {
    const items = lessons
        .filter(l => l.time)
        .map(l => {
            const start = toMinutes(l.time);
            const end = start + (l.duration_minutes || 60);
            return { lesson: l, start, end };
        })
        .sort((a, b) => a.start - b.start || a.end - b.end);

    // Assign columns for overlap groups
    const columns = [];
    for (const item of items) {
        let placed = false;
        for (let c = 0; c < columns.length; c++) {
            const col = columns[c];
            if (col.length === 0 || col[col.length - 1].end <= item.start) {
                col.push(item);
                item.col = c;
                placed = true;
                break;
            }
        }
        if (!placed) {
            columns.push([item]);
            item.col = columns.length - 1;
        }
    }
    // For each item, calculate total overlap group width
    const totalCols = columns.length || 1;
    return items.map(it => ({ ...it, totalCols }));
}

export default function DayTimeline({
    lessons,
    getStudent,
    onEdit,
    onDelete,
    onWhatsapp,
    isToday,
}) {
    const hours = Array.from({ length: END_HOUR - START_HOUR + 1 }, (_, i) => START_HOUR + i);
    const layout = computeLayout(lessons);

    // Now indicator
    const now = new Date();
    const nowMinutes = now.getHours() * 60 + now.getMinutes();
    const showNowLine = isToday && nowMinutes >= START_HOUR * 60 && nowMinutes <= END_HOUR * 60;
    const nowTop = ((nowMinutes - START_HOUR * 60) / 60) * HOUR_HEIGHT;

    return (
        <div className="relative bg-[#1A202C] rounded-lg border border-[#4A5568] overflow-hidden" data-testid="day-timeline">
            <div className="flex">
                {/* Hour rail */}
                <div className="w-14 flex-shrink-0 border-r border-[#4A5568] bg-[#151B26]">
                    {hours.map(h => (
                        <div
                            key={h}
                            className="text-[11px] text-gray-500 text-right pr-2 -mt-2 tabular-nums"
                            style={{ height: HOUR_HEIGHT }}
                        >
                            {String(h).padStart(2, '0')}:00
                        </div>
                    ))}
                </div>

                {/* Grid */}
                <div className="relative flex-1" style={{ height: TOTAL_HEIGHT }}>
                    {/* Hour lines */}
                    {hours.map((h, i) => (
                        <div
                            key={h}
                            className={`absolute left-0 right-0 border-t ${i === 0 ? 'border-transparent' : 'border-[#2D3748]'}`}
                            style={{ top: i * HOUR_HEIGHT }}
                        />
                    ))}
                    {/* Half-hour dashed */}
                    {hours.slice(0, -1).map((h, i) => (
                        <div
                            key={`half-${h}`}
                            className="absolute left-0 right-0 border-t border-dashed border-[#2D3748]/60"
                            style={{ top: i * HOUR_HEIGHT + HOUR_HEIGHT / 2 }}
                        />
                    ))}

                    {/* Now line */}
                    {showNowLine && (
                        <div
                            className="absolute left-0 right-0 z-20 pointer-events-none"
                            style={{ top: nowTop }}
                            data-testid="timeline-now-line"
                        >
                            <div className="flex items-center gap-1">
                                <span className="w-2 h-2 rounded-full bg-red-500 shadow-[0_0_10px_2px_rgba(239,68,68,0.7)] animate-pulse -ml-1" />
                                <div className="flex-1 h-[2px] bg-red-500/70" />
                                <span className="text-[10px] font-semibold text-red-400 bg-[#1A202C] px-1 rounded">
                                    {String(now.getHours()).padStart(2, '0')}:{String(now.getMinutes()).padStart(2, '0')}
                                </span>
                            </div>
                        </div>
                    )}

                    {/* Lesson blocks */}
                    {layout.map(({ lesson, start, end, col, totalCols }) => {
                        const top = ((start - START_HOUR * 60) / 60) * HOUR_HEIGHT;
                        const height = Math.max(28, ((end - start) / 60) * HOUR_HEIGHT - 4);
                        const student = getStudent(lesson.student_id);
                        const theme = student ? getPackageTheme(student.package_type) : { primary: '#D4A537', auraSoft: 'rgba(212,165,55,0.35)' };
                        const width = `calc(${100 / totalCols}% - 6px)`;
                        const left = `calc(${(col * 100) / totalCols}% + 4px)`;
                        const pending = lesson.confirmation_status === 'pending';
                        const cancelled = lesson.confirmation_status === 'cancelled';
                        const endTime = (() => {
                            const [h, m] = lesson.time.split(':').map(Number);
                            const t = h * 60 + (m || 0) + (lesson.duration_minutes || 60);
                            return `${String(Math.floor(t / 60) % 24).padStart(2, '0')}:${String(t % 60).padStart(2, '0')}`;
                        })();

                        return (
                            <div
                                key={lesson.id}
                                className="absolute rounded-md border overflow-hidden text-white group hover:z-10 transition-all"
                                style={{
                                    top,
                                    height,
                                    left,
                                    width,
                                    background: `linear-gradient(135deg, ${theme.primary}33 0%, ${theme.primary}1A 100%)`,
                                    borderColor: cancelled ? '#EF4444' : theme.primary,
                                    boxShadow: `0 0 22px -8px ${theme.auraSoft || theme.primary}`,
                                    opacity: cancelled ? 0.55 : 1,
                                }}
                                data-testid={`timeline-lesson-${lesson.id}`}
                            >
                                <div
                                    className="absolute inset-y-0 left-0 w-1"
                                    style={{ backgroundColor: theme.primary }}
                                />
                                <div className="p-2 h-full flex flex-col justify-between gap-1 pl-3">
                                    <div className="min-w-0">
                                        <div className="flex items-center gap-1.5 text-[11px] font-bold tabular-nums" style={{ color: theme.primary }}>
                                            {lesson.time} – {endTime}
                                            {pending && (
                                                <span className="inline-flex items-center gap-0.5 text-[9px] px-1 py-0.5 rounded bg-amber-500/20 text-amber-300 border border-amber-500/40" title="Wacht op bevestiging leerling">
                                                    <Warning size={9} weight="fill" />
                                                </span>
                                            )}
                                        </div>
                                        <div className="text-xs font-semibold text-white truncate flex items-center gap-1">
                                            <User size={11} weight="fill" style={{ color: theme.primary }} />
                                            {student?.name || 'Onbekend'}
                                        </div>
                                        {lesson.notes && height > 60 && (
                                            <div className="text-[10px] text-gray-300 mt-0.5 line-clamp-2">
                                                {lesson.notes}
                                            </div>
                                        )}
                                    </div>
                                    {height > 44 && (
                                        <div className="flex items-center justify-end gap-0.5 opacity-0 group-hover:opacity-100 transition-opacity">
                                            <button
                                                type="button"
                                                onClick={(e) => { e.stopPropagation(); onWhatsapp?.(lesson); }}
                                                className="w-6 h-6 rounded flex items-center justify-center bg-green-500/20 hover:bg-green-500/40 text-green-300"
                                                data-testid={`timeline-whatsapp-${lesson.id}`}
                                                title="WhatsApp"
                                            >
                                                <WhatsappLogo size={12} weight="fill" />
                                            </button>
                                            <button
                                                type="button"
                                                onClick={(e) => { e.stopPropagation(); onEdit?.(lesson); }}
                                                className="w-6 h-6 rounded flex items-center justify-center bg-blue-500/20 hover:bg-blue-500/40 text-blue-300"
                                                data-testid={`timeline-edit-${lesson.id}`}
                                                title="Wijzig"
                                            >
                                                <PencilSimple size={12} />
                                            </button>
                                            <button
                                                type="button"
                                                onClick={(e) => { e.stopPropagation(); onDelete?.(lesson.id); }}
                                                className="w-6 h-6 rounded flex items-center justify-center bg-red-500/20 hover:bg-red-500/40 text-red-300"
                                                data-testid={`timeline-delete-${lesson.id}`}
                                                title="Verwijder"
                                            >
                                                <Trash size={12} />
                                            </button>
                                        </div>
                                    )}
                                </div>
                            </div>
                        );
                    })}
                </div>
            </div>
        </div>
    );
}
